import { Component, computed, input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { HlmButtonImports } from '@mb/ui/button';

import type { Action } from '../table/action';

/**
 * Shared chrome for a record shown in the preview sheet.
 *
 * One line at the top saying which record is open, then the actions, then the
 * record. The big banner this replaced — accent bar, tinted band, 2xl title —
 * cost about 130px before the first field, on a sheet that is often read on a
 * phone, and said no more than the line does.
 *
 * `portrait` brings the banner back for the two places that earn it: a person
 * and a user have a face, and the picture is worth the height. Of the twenty-one
 * views using this shell, one ever filled the picture slot.
 */
@Component({
  selector: 'app-details-shell',
  standalone: true,
  imports: [TranslocoPipe, HlmButtonImports],
  host: { class: 'flex min-h-0 flex-1 flex-col' },
  styles: [
    `

      .details-visual-frame:has(.details-visual-slot:empty) {
        display: none;
      }
    `,
  ],
  template: `
    @if (bare()) {
      <!-- Nothing: a shell inside another one would be a frame in a frame. -->
    } @else if (portrait()) {
      <div class="h-2 bg-primary"></div>

      <div class="border-b border-border bg-secondary/40 px-4 py-5 sm:px-6">
        <div class="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div class="details-visual-frame shrink-0">
            <div
              class="inline-flex overflow-hidden rounded-xl border-2 border-background shadow-xs"
            >
              <div class="details-visual-slot size-24 sm:size-28">
                <ng-content select="[detailsVisual]" />
              </div>
            </div>
          </div>

          <div class="min-w-0 flex-1 sm:pl-1">
            <h3 class="text-2xl font-bold text-foreground">
              {{ title() | transloco }}
            </h3>
            @if (subtitle(); as sub) {
              <p class="text-sm text-muted-foreground">{{ sub | transloco }}</p>
            }
          </div>
        </div>
      </div>
    } @else {
      <!--
        The sheet draws its ✕ absolutely at the top right, so this row keeps
        clear of it.
      -->
      <!--
        Tall enough for the ✕ the sheet draws over it: that button is 32px at
        16px from the top, so its centre is at 32px. A shorter row puts this
        line above the ✕ and hard against the divider — which is how it looked
        before. A 64px row centres the two against each other.
      -->
      <div
        class="flex min-h-16 shrink-0 items-center border-b border-border bg-background pe-12 ps-4 sm:ps-6"
      >
        <div class="flex min-w-0 items-baseline gap-2">
          <!--
            A breadcrumb where there is a trail to show, the record's name where
            there is not. Both land in the same row, and a caller uses one.
          -->
          <ng-content select="[detailsTrail]" />
          @if (title(); as name) {
            <h3 class="truncate font-medium text-foreground">
              {{ name | transloco }}
            </h3>
          }
          @if (subtitle(); as sub) {
            <p class="truncate text-sm text-muted-foreground">
              {{ sub | transloco }}
            </p>
          }
        </div>
      </div>
    }

    <!--
      Above the record, not below it: a form long enough to scroll puts buttons
      at its foot out of sight, and on a phone out of reach.

      Right-aligned, with anything destructive pushed to the far left — a
      button that cannot be undone should not sit beside the one that saves.

      Two ways in, because there are two kinds of caller. A record being viewed
      declares what can be done to it as data; a form has buttons that carry
      state — a save that spins, a cancel that is a link — and projects them.
      The row hides itself when a caller uses neither.
    -->
    <div
      class="flex flex-wrap items-center justify-end gap-2 border-b border-border px-4 py-3 empty:hidden sm:px-6"
    >
      <ng-content select="[detailsActions]" />
      @for (action of actions(); track action.label) {
        <button
          type="button"
          hlmBtn
          size="sm"
          [variant]="variantFor(action)"
          [class.me-auto]="action.linkType === 'danger'"
          [disabled]="action.disabled"
          (click)="action.function()"
        >
          @if (action.icon) {
            <span class="material-icons text-base" aria-hidden="true">{{
              action.icon
            }}</span>
          }
          {{ action.label | transloco }}
        </button>
      }
    </div>

    <!--
      Only the record scrolls. The row above it does not, so the buttons stay
      reachable in a form long enough to need scrolling — which is what they
      were moved to the top for.
    -->
    <div [class]="bodyClasses()">
      <ng-content />
    </div>
  `,
})
export class DetailsShellComponent {
  /** Optional: a caller may project a breadcrumb into `detailsTrail` instead. */
  readonly title = input<string>();
  readonly subtitle = input<string>();
  readonly actions = input<Action[]>([]);

  /**
   * Show the record's name as a banner, with room for a picture.
   *
   * Opt-in, because it is worth its height only where a record has a face.
   * Everything else gets the one-line header.
   */
  readonly portrait = input(false);

  /**
   * Rendered inside another shell rather than as a sheet of its own.
   *
   * Drops the header, the padding and the scroll container: the host shell
   * already has all three, and a second set is a frame inside a frame.
   */
  readonly bare = input(false);

  protected readonly bodyClasses = computed(() =>
    this.bare()
      ? 'flex min-h-0 flex-1 flex-col gap-6'
      : 'flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-4 sm:px-6',
  );

  /**
   * The button style an action asks for.
   *
   * Mapped onto the button component rather than styled here: the colours used
   * to be written into the markup as `bg-red-50 text-red-700`, which no theme
   * and no dark mode could follow.
   */
  protected variantFor(action: Action): 'destructive' | 'secondary' | 'outline' {
    if (action.linkType === 'danger') return 'destructive';
    if (action.linkType === 'warning') return 'secondary';
    return 'outline';
  }
}
